import Accordions from '@/common/components/Accordions';
import Box from '@/common/components/Box';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName';
import MaintenanceWorkflowForwardingShow from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/MaintenanceWorkflowForwardingShow'
import MaintenanceWorkflowForwardingShowLoading from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/MaintenanceWorkflowForwardingShowLoading'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { useMaintenanceWorkflowForwardings } from '@/features/maintenanceWorkflow/pages/Show/hooks/useMaintenanceWorkflowForwardings'
import { MaintenanceWorkflowForwardingsData } from '@/features/maintenanceWorkflow/pages/Show/types/maintenanceWorkflowForwardings.type'
import { MaintenanceWorkflowData } from '@/features/maintenanceWorkflow/types/maintenanceWorkflows.type'
import { Building2Icon } from 'lucide-react';
import { useMemo } from 'react'
import { maintenanceForwardingsDirectiveEntryKlasses } from '../../../features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.constant';
import { formatDateTime } from '@/common/utils/formatters';

interface MaintenanceWorkflowForwardingsTabProps {
  workflow?: MaintenanceWorkflowData
  directiveEntryKlass: MaintenanceForwardingsDirectiveEntryKlass
}

const MaintenanceWorkflowForwardingsTab = ({
  workflow,
  directiveEntryKlass,
}: MaintenanceWorkflowForwardingsTabProps) => {
  const {
    data: forwardings,
    isPending: forwardingsIsPending,
    error: forwardingsError = {} as any,
    refetch: refetchForwardings,
    dataUpdatedAt: forwardingsUpdatedAt,
  } = useMaintenanceWorkflowForwardings<MaintenanceWorkflowForwardingsData[]>({
    workflowId: workflow?.id,
    filters: {
      directiveEntryType: directiveEntryKlass,
    },
  })

  const forwarding = useMemo(() => {
    return forwardings?.[0]
  }, [forwardingsUpdatedAt])

  const previousForwardings = useMemo(() => {
    return (forwardings || []).slice(1);
  }, [forwardingsUpdatedAt]);

  if (forwardingsIsPending) {
    return <MaintenanceWorkflowForwardingShowLoading />
  }

  return (
    <ErrorMessageWrapper className="flex-1" statusCode={forwardingsError?.status} refetchFunction={refetchForwardings}>
      <div className="flex-1 overflow-auto flex flex-col gap-4">
        {forwarding && (
          <MaintenanceWorkflowForwardingShow
            key={forwarding.id}
            workflow={workflow}
            forwardingId={forwarding.id}
            directiveEntryKlass={directiveEntryKlass}
          />
        )}

        {previousForwardings?.length > 0 && (
          <Box className="flex flex-col gap-4 p-4 bg-blue-100/10">
            <p className="font-bold text-lg">
              {({
                [MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry]: 'Diagnósticos e orçamentos anteriores',
                [MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry]: 'Execuções anteriores',
              } as Partial<Record<MaintenanceForwardingsDirectiveEntryKlass, string>>)[directiveEntryKlass]
                || 'Encaminhamentos anteriores'}
            </p>

            <Accordions
              className="p-0"
              triggerClassName="p-4"
              items={previousForwardings?.map((forwarding, index) => ({
                index,
                id: forwarding.id,
                head: (
                  <div className="flex flex-col items-start gap-1.5">
                    {forwarding?.establishment && (
                      <div className="flex items-center gap-3">
                        <ImageWithDisplayName
                          placeholderIcon={Building2Icon}
                          iconClassName="size-[18px]"
                          imageUrl={forwarding?.establishment?.general_information?.company_logo?.url}
                          displayName={forwarding?.establishment?.general_information?.fantasy_name}
                        />
                      </div>)
                    }

                    <div className="flex items-center gap-0.5 text-xs italic">
                      <span>
                        {maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.status?.[forwarding?.status]?.label}
                      </span>

                      {forwarding.created_at && (
                        <>
                          <span>
                            •
                          </span>

                          <span className="text-white-400">
                            {formatDateTime(new Date(forwarding?.created_at))}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ),
                body: (
                  <MaintenanceWorkflowForwardingShow
                    key={forwarding.id}
                    workflow={workflow}
                    forwardingId={forwarding.id}
                    directiveEntryKlass={directiveEntryKlass}
                    noActions
                  />
                )
              }))}
            />
          </Box>
        )}
      </div>
    </ErrorMessageWrapper>
  )
}

export default MaintenanceWorkflowForwardingsTab
